<script lang="ts">
  import Icon from 'shared/javascripts/components/Icon.svelte'
  import MobileMenuHeader from './MobileMenuHeader.svelte'
  import ScrollableSubmenu from './ScrollableSubmenu.svelte'
  import { publishLevelHeight } from 'frontend/inertia/actions/publishLevelHeight'
  import { down } from 'shared/javascripts/util/breakpoints'
  import { resolveIconUrl } from 'frontend/inertia/util/resolveIcon'
  import { trackNavigationItem } from 'frontend/inertia/util/trackNav'
  import { isCurrentItem } from 'frontend/inertia/util/currentPath'
  import {
    isMenuLink,
    type AuthMenuAction,
    type AuthMenuItem,
    type UserMenuEntry,
  } from './user_menu'
  import LogoutForm from './LogoutForm.svelte'
  import { linkAction } from 'shared/javascripts/components/link_action'
  import { openGdprManager } from 'frontend/shared/javascripts/util/gdpr_consent'
  import { postNativeMessage } from 'frontend/shared/javascripts/native_app/messages'
  import { readInertiaConfig } from 'frontend/inertia/config'
  import chevronRightIcon from 'shared/images/icons/chevron-right.svg'

  let {
    items,
    close,
    currentPath = '',
  }: {
    items: UserMenuEntry[]
    close: () => void
    currentPath?: string
  } = $props()

  let openItem: AuthMenuItem | null = $state(null)

  const anyItemHasChildren = $derived(items.some((item) => isMenuLink(item) && item.children))
  const childrenOpen = $derived(openItem !== null)

  $effect(() => {
    const mediaQuery = window.matchMedia(down('header-medium'))
    function onChange(event: MediaQueryListEvent) {
      if (!event.matches) closeChildren()
    }
    mediaQuery.addEventListener('change', onChange)
    return () => mediaQuery.removeEventListener('change', onChange)
  })

  function isMobileMenuLayout() {
    return window.matchMedia(down('header-medium')).matches
  }

  function closeChildren() {
    openItem = null
  }

  function opensSubmenu(item: AuthMenuItem) {
    return !!item.children && isMobileMenuLayout()
  }

  function onItemClick(event: MouseEvent, item: AuthMenuItem) {
    trackNavigationItem(event)
    if (opensSubmenu(item)) {
      event.preventDefault()
      openItem = item
    } else {
      close()
    }
  }

  function runAction(action: AuthMenuAction['action']) {
    close()
    if (action === 'gdprSettings') {
      openGdprManager(readInertiaConfig().privacyManagerId)
    } else {
      postNativeMessage('startIntro')
    }
  }

  function onChildClick(event: MouseEvent) {
    trackNavigationItem(event)
    close()
  }

  function onMobileClose() {
    closeChildren()
    close()
  }
</script>

<div class="menu">
  <MobileMenuHeader
    label={openItem ? openItem.label : 'Menü'}
    showBack={childrenOpen}
    onBack={closeChildren}
    onClose={onMobileClose}
  />

  <div class="level" use:publishLevelHeight>
    <ScrollableSubmenu>
      <ul class="rows rowsTop">
        <!-- tracking: navigation.item_click -->
        {#each items as item (item.label)}
          {@const iconUrl = resolveIconUrl(item.icon)}
          <li class="entry">
            {#if !isMenuLink(item)}
              <button
                type="button"
                class="user-dropdown-item"
                onclick={() => runAction(item.action)}
              >
                {#if iconUrl}
                  <span class="entry--icon"><Icon src={iconUrl} /></span>
                {/if}
                {item.label}
              </button>
            {:else if item.method === 'delete'}
              <LogoutForm action={item.href}>
                <button type="submit" class="user-dropdown-item" onclick={trackNavigationItem}>
                  {#if iconUrl}
                    <span class="entry--icon"><Icon src={iconUrl} /></span>
                  {/if}
                  {item.label}
                </button>
              </LogoutForm>
            {:else}
              <a
                class="user-dropdown-item"
                class:active={item.href === openItem?.href}
                class:current={isCurrentItem(item, currentPath)}
                href={item.href}
                target={item.target}
                rel={item.target === '_blank' ? 'noopener' : undefined}
                up-instant={item.children ? 'false' : undefined}
                use:linkAction
                onbefore={(event: Event) => {
                  if (opensSubmenu(item)) event.preventDefault()
                }}
                onclick={(event) => onItemClick(event, item)}
              >
                {#if iconUrl}
                  <span class="entry--icon"><Icon src={iconUrl} /></span>
                {/if}
                {item.label}
                {#if item.children}
                  <Icon src={chevronRightIcon} class="caret" />
                {/if}
              </a>
            {/if}
          </li>
        {/each}
      </ul>
    </ScrollableSubmenu>

    {#if anyItemHasChildren}
      <div class="submenu" class:open={childrenOpen}>
        <ScrollableSubmenu open={childrenOpen}>
          <ul class="rows rowsChildren">
            {#each openItem?.children ?? [] as child (child.label)}
              {@const childIconUrl = resolveIconUrl(child.icon)}
              <li class="entry">
                {#if child.href}
                  <a
                    class="user-dropdown-item"
                    class:current={isCurrentItem(child, currentPath)}
                    href={child.href}
                    use:linkAction
                    onclick={onChildClick}
                  >
                    {#if childIconUrl}
                      <span class="entry--icon"><Icon src={childIconUrl} /></span>
                    {/if}
                    {child.label}
                  </a>
                {:else}
                  <span class="user-dropdown-item">
                    {#if childIconUrl}
                      <span class="entry--icon"><Icon src={childIconUrl} /></span>
                    {/if}
                    {child.label}
                  </span>
                {/if}
              </li>
            {/each}
          </ul>
        </ScrollableSubmenu>
      </div>
    {/if}
  </div>
</div>

<style>
  .menu {
    position: relative;
  }

  .level {
    position: relative;
    width: max-content;
  }

  .rows {
    margin: 0;
    padding: 0;
    list-style: none;
    min-width: 200px;
  }

  /* Not :not(.open): a resize past the breakpoint can leave .open applied at desktop width. */
  .submenu {
    display: none;
  }

  @media (--header-medium-down) {
    .level,
    .rows {
      width: 100%;
    }

    .submenu {
      display: block;
      position: absolute;
      top: 0;
      left: auto;
      right: calc(-100% - 10px);
      z-index: 3;
      width: 100%;
      background-color: var(--color-white);
      border-bottom-left-radius: var(--border-radius-lg);
      border-bottom-right-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-1);
      transition: right 0.3s ease-out;
      min-height: var(--level-height, 0px);
    }

    .submenu:not(.open) {
      box-shadow: none;
    }

    .submenu.open {
      right: 0;
    }
  }

  .entry {
    display: flex;
    align-items: stretch;
    min-height: 50px;
  }

  .entry + .entry {
    border-top: 1px solid var(--color-border);
  }

  .entry:first-child,
  .entry:last-child {
    min-height: 55px;
  }

  .entry:first-child .user-dropdown-item {
    padding-top: var(--space-1);
    border-top-left-radius: var(--submenu-radius-top);
    border-top-right-radius: var(--submenu-radius-top);
  }

  .entry:last-child .user-dropdown-item {
    padding-bottom: var(--space-1);
    border-bottom-left-radius: var(--submenu-radius-bottom);
    border-bottom-right-radius: var(--submenu-radius-bottom);
  }

  .user-dropdown-item {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    width: 100%;
    padding: 0 var(--space-6) 0 var(--space-8);
    border: none;
    background: none;
    color: var(--color-arsenic);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-base);
  }

  .user-dropdown-item:hover,
  .user-dropdown-item.active {
    background-color: var(--color-primary-brightest);
  }

  .user-dropdown-item.current,
  .user-dropdown-item.current .entry--icon {
    color: var(--color-cerulean);
  }

  .user-dropdown-item:focus-visible {
    outline-offset: -2px;
  }

  .entry--icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--color-arsenic);
  }

  .entry--icon :global(.icon) {
    --size: 24px;
  }

  .user-dropdown-item :global(.caret) {
    display: none;
  }

  @media (--header-medium-down) {
    .user-dropdown-item :global(.caret) {
      --size: 16px;
      display: inline-flex;
      margin-left: auto;
    }
  }
</style>
